import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { Snackbar, Alert, IconButton, Box, Typography } from "@mui/material";
import {
  Close as CloseIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { INotification, INotificationFilters } from "../types";
import { getNotifications, markNotificationAsRead } from "../services";

interface NotificationContextType {
  unreadCount: number;
  notifications: INotification[];
  loadNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  showNotificationToast: (notification: INotification) => void;
  loadNotificationsAfterLogin: () => Promise<void>;
  refreshNotifications: () => Promise<void>; // Nova função para forçar atualização
  lastUpdate: number; // Timestamp da última atualização para observers
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

interface ToastNotification extends INotification {
  toastId: string;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toastQueue, setToastQueue] = useState<ToastNotification[]>([]);
  const [currentToast, setCurrentToast] = useState<ToastNotification | null>(
    null
  );
  const [lastCheck, setLastCheck] = useState<Date>(new Date());
  const [lastUpdate, setLastUpdate] = useState<number>(Date.now());

  // Verificar se uma notificação deve ser exibida (horário chegou)
  const shouldShowNotification = useCallback((notification: INotification) => {
    if (!notification.isActive || notification.isRead) return false;

    const notificationDateTime = new Date(
      `${notification.scheduledDate.split("T")[0]}T${notification.scheduledTime}:00`
    );
    const now = new Date();

    return notificationDateTime <= now;
  }, []);

  // Carregar notificações (versão estável para evitar loops)
  const loadNotifications = useCallback(async () => {
    try {
      // Verificar se o usuário está autenticado
      const token = localStorage.getItem("token");
      if (!token) {
        // Limpar estado quando não autenticado
        setNotifications([]);
        setUnreadCount(0);
        setToastQueue([]);
        return;
      }

      const filters: INotificationFilters = {
        page: 1,
        limit: 100,
        isActive: true,
      };

      const response = await getNotifications(filters);
      setNotifications(response.data);

      // Contar não lidas usando função inline para evitar dependência
      const checkNotificationTime = (notification: INotification) => {
        if (!notification.isActive || notification.isRead) return false;
        const notificationDateTime = new Date(
          `${notification.scheduledDate.split("T")[0]}T${notification.scheduledTime}:00`
        );
        return notificationDateTime <= new Date();
      };

      const unread = response.data.filter(checkNotificationTime);
      setUnreadCount(unread.length);

      // Verificar notificações que devem aparecer como toast
      const now = new Date();
      const newToasts: ToastNotification[] = [];

      response.data.forEach((notification) => {
        if (checkNotificationTime(notification)) {
          const notificationDateTime = new Date(
            `${notification.scheduledDate.split("T")[0]}T${notification.scheduledTime}:00`
          );

          // Se a notificação é nova (depois do último check)
          // Usar um controle mais simples sem dependência de lastCheck
          if (!notification.isRead && notification.isActive) {
            newToasts.push({
              ...notification,
              toastId: `${notification.id}-${Date.now()}`,
            });
          }
        }
      });

      // Limitar toasts para evitar spam (máximo 3 por vez)
      if (newToasts.length > 0) {
        setToastQueue((prev) => {
          const combined = [...prev, ...newToasts.slice(0, 3)];
          return combined.slice(0, 5); // Máximo 5 na fila
        });
      }

      setLastCheck(now);
    } catch (error: any) {
      // Apenas log de erro se não for um problema de autenticação
      if (error?.response?.status !== 401) {
      }
    }
  }, []); // Sem dependências para evitar loop infinito

  // Marcar como lida
  const markAsRead = useCallback(async (id: string) => {
    try {
      // Verificar se o usuário está autenticado
      const token = localStorage.getItem("token");
      if (!token) {
        return; // Não executar se não estiver autenticado
      }

      await markNotificationAsRead(id);

      // Atualizar estado local sem recarregar tudo
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === id ? { ...notif, isRead: true } : notif
        )
      );

      // Atualizar contador
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error: any) {
      // Apenas log de erro se não for um problema de autenticação
      if (error?.response?.status !== 401) {
      }
    }
  }, []);

  // Mostrar toast manualmente
  const showNotificationToast = useCallback((notification: INotification) => {
    const toast: ToastNotification = {
      ...notification,
      toastId: `manual-${notification.id}-${Date.now()}`,
    };
    setToastQueue((prev) => [...prev, toast]);
  }, []);

  // Forçar atualização imediata das notificações (para criar/editar)
  const refreshNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return;
      }

      //

      const filters: INotificationFilters = {
        page: 1,
        limit: 100,
        isActive: true,
      };

      const response = await getNotifications(filters);
      setNotifications(response.data);

      // Atualizar contador de não lidas
      const checkNotificationTime = (notification: INotification) => {
        if (!notification.isActive || notification.isRead) return false;
        const notificationDateTime = new Date(
          `${notification.scheduledDate.split("T")[0]}T${notification.scheduledTime}:00`
        );
        return notificationDateTime <= new Date();
      };

      const unread = response.data.filter(checkNotificationTime);
      setUnreadCount(unread.length);

      // Atualizar timestamp para notificar observers
      setLastUpdate(Date.now());

      //
    } catch (error: any) {
      if (error?.response?.status !== 401) {
      }
    }
  }, []);

  // Carregar notificações após login (força exibição de toasts para não lidas)
  const loadNotificationsAfterLogin = useCallback(async () => {
    try {
      // Verificar se o usuário está autenticado
      const token = localStorage.getItem("token");
      if (!token) {
        return;
      }

      const filters: INotificationFilters = {
        page: 1,
        limit: 100,
        isActive: true,
      };

      const response = await getNotifications(filters);
      setNotifications(response.data);

      // Contar não lidas que já devem ter sido exibidas
      const unreadDueNotifications = response.data.filter(
        (notification) =>
          shouldShowNotification(notification) && !notification.isRead
      );

      setUnreadCount(unreadDueNotifications.length);

      // Criar toasts para todas as notificações não lidas que já deveriam ter sido exibidas
      const newToasts: ToastNotification[] = [];
      unreadDueNotifications.forEach((notification) => {
        newToasts.push({
          ...notification,
          toastId: `login-${notification.id}-${Date.now()}`,
        });
      });

      if (newToasts.length > 0) {
        setToastQueue((prev) => [...prev, ...newToasts]);
      }

      setLastCheck(new Date());
    } catch (error: any) {
      // Apenas log de erro se não for um problema de autenticação
      if (error?.response?.status !== 401) {
      }
    }
  }, [shouldShowNotification]);

  // Processar fila de toasts
  useEffect(() => {
    if (toastQueue.length > 0 && !currentToast) {
      const [nextToast, ...remainingQueue] = toastQueue;
      setCurrentToast(nextToast);
      setToastQueue(remainingQueue);
    }
  }, [toastQueue, currentToast]);

  // Fechar toast atual
  const handleCloseToast = useCallback(
    (markAsReadOnClose = false) => {
      if (currentToast && markAsReadOnClose) {
        markAsRead(currentToast.id);
      }
      setCurrentToast(null);
    },
    [currentToast, markAsRead]
  );

  // Polling de notificações com controle mais restritivo
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    let isActive = true;

    const pollNotifications = async () => {
      if (!isActive) return;

      const token = localStorage.getItem("token");
      if (!token) {
        setNotifications([]);
        setUnreadCount(0);
        setToastQueue([]);
        return;
      }

      try {
        const filters = { page: 1, limit: 100, isActive: true };
        const response = await getNotifications(filters);

        if (!isActive) return; // Verificar novamente após async

        setNotifications(response.data);

        // Contar não lidas
        const checkTime = (notification: INotification) => {
          if (!notification.isActive || notification.isRead) return false;
          const notificationDateTime = new Date(
            `${notification.scheduledDate.split("T")[0]}T${notification.scheduledTime}:00`
          );
          return notificationDateTime <= new Date();
        };

        const unread = response.data.filter(checkTime);
        setUnreadCount(unread.length);

        // Limitar toasts excessivos
        const newToasts = unread
          .filter((n) => !n.isRead && n.isActive)
          .slice(0, 2) // Máximo 2 toasts por poll
          .map((n) => ({ ...n, toastId: `${n.id}-${Date.now()}` }));

        if (newToasts.length > 0) {
          setToastQueue((prev) => [...prev.slice(-3), ...newToasts]); // Max 5 total
        }
      } catch (error: any) {
        if (error?.response?.status !== 401 && isActive) {
        }
      }
    };

    // Verificar se há token antes de iniciar
    const token = localStorage.getItem("token");
    if (token) {
      //

      // Carregar imediatamente
      pollNotifications();

      // Polling a cada 3 minutos (mais conservador)
      interval = setInterval(() => {
        pollNotifications();
      }, 180000); // 3 minutos
    }

    return () => {
      isActive = false;
      if (interval) {
        clearInterval(interval);
        //
      }
    };
  }, []); // Sem dependências para evitar re-renders

  // Verificar quando a aba volta ao foco
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadNotifications]);

  const contextValue: NotificationContextType = {
    unreadCount,
    notifications,
    loadNotifications,
    markAsRead,
    showNotificationToast,
    loadNotificationsAfterLogin,
    refreshNotifications,
    lastUpdate,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}

      {/* Toast de Notificação */}
      <Snackbar
        open={!!currentToast}
        autoHideDuration={8000}
        onClose={() => handleCloseToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity="info"
          variant="filled"
          sx={{
            minWidth: 350,
            maxWidth: 500,
            "& .MuiAlert-message": {
              flex: 1,
            },
          }}
          action={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => handleCloseToast(true)}
                title="Marcar como lida"
              >
                <ViewIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => handleCloseToast(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              {currentToast?.title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {currentToast?.summary}
            </Typography>
            {currentToast?.pet && (
              <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
                Pet: {currentToast.pet.name}
                {currentToast.pet.breed && ` (${currentToast.pet.breed})`}
              </Typography>
            )}
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Agendado para{" "}
              {currentToast &&
                new Date(currentToast.scheduledDate).toLocaleDateString(
                  "pt-BR"
                )}{" "}
              às {currentToast?.scheduledTime}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
